import { useTranslation } from 'react-i18next'
import { TNavigationLocales } from 'src/types/locales'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { getFormattedDateWithHours } from 'src/utils/date-formatter.util'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import {
  EPatientFollowupDocumentStatus,
  TPatientFollowupDocument,
} from './use-patient-followup-documents.hook'

const isPatientFollowupDocumentAvailable = (document: TPatientFollowupDocument) =>
  document.status === EPatientFollowupDocumentStatus.RECEIVED ||
  document.status === EPatientFollowupDocumentStatus.WAIVED

const hasPatientFollowupDocumentFile = (document: TPatientFollowupDocument) =>
  document.status === EPatientFollowupDocumentStatus.RECEIVED

const getPatientFollowupDocumentName = (document: TPatientFollowupDocument, locale: TNavigationLocales) =>
  getTranslatedLabel(locale, document.variableIntlLabel ?? undefined) || document.variableName

const usePatientFollowupDocumentDisplay = (document: TPatientFollowupDocument) => {
  const { t, i18n } = useTranslation()
  const { displayTimezone } = useTimezonePreference()
  const locale = i18n.language as TNavigationLocales

  const formatDate = (date: string) => getFormattedDateWithHours(t, date, false, displayTimezone)

  const statusLabelByStatus: Record<EPatientFollowupDocumentStatus, string> = {
    [EPatientFollowupDocumentStatus.RECEIVED]: t('components.patient-documents-modal.status-available'),
    [EPatientFollowupDocumentStatus.WAIVED]: t('components.patient-followup-document.marked-received'),
    [EPatientFollowupDocumentStatus.REQUESTED]: t('components.patient-followup-document.pending'),
    [EPatientFollowupDocumentStatus.EXPECTED]: t('components.patient-followup-document.pending'),
  }

  const dateLabelByStatus: Record<EPatientFollowupDocumentStatus, string | null> = {
    [EPatientFollowupDocumentStatus.RECEIVED]: document.receivedAt
      ? t('components.patient-followup-document.received-on', { date: formatDate(document.receivedAt) })
      : null,
    [EPatientFollowupDocumentStatus.WAIVED]: document.waivedAt
      ? t('components.patient-followup-document.marked-received-on', { date: formatDate(document.waivedAt) })
      : t('components.patient-followup-document.marked-received-subtitle'),
    [EPatientFollowupDocumentStatus.REQUESTED]: document.requestedAt
      ? t('components.patient-followup-document.requested-on', { date: formatDate(document.requestedAt) })
      : null,
    [EPatientFollowupDocumentStatus.EXPECTED]: t('components.patient-followup-document.not-requested-yet'),
  }

  return {
    name: getPatientFollowupDocumentName(document, locale),
    statusLabel: statusLabelByStatus[document.status],
    dateLabel: dateLabelByStatus[document.status],
    isAvailable: isPatientFollowupDocumentAvailable(document),
    hasFile: hasPatientFollowupDocumentFile(document),
  }
}

export {
  getPatientFollowupDocumentName,
  hasPatientFollowupDocumentFile,
  isPatientFollowupDocumentAvailable,
  usePatientFollowupDocumentDisplay,
}
